import { createContext, useCallback, useContext, useEffect, useState } from "react";

/**
 * Admin auth state. The backend session cookie is the source of truth; this
 * provider only caches the latest admin identity returned by GET /api/admin/auth/me.
 *
 * Exposes:
 *   - admin:       { admin_user_id, email, name, picture } | null
 *   - status:      "loading" | "authenticated" | "unauthenticated"
 *   - refresh():   re-hit /me (after login, after protected-route mount)
 *   - logout():    POST /logout and flip to unauthenticated
 */

const AdminAuthContext = createContext(null);
// API calls use relative `/api/...` paths. See ContentProvider.jsx for
// the rationale.

export function AdminAuthProvider({ children }) {
  const [admin, setAdmin] = useState(null);
  const [status, setStatus] = useState("loading");

  const refresh = useCallback(async () => {
    try {
      const res = await fetch(`/api/admin/auth/me`, {
        method: "GET",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
      if (!res.ok) throw new Error(`me:${res.status}`);
      const data = await res.json();
      setAdmin(data.admin || null);
      setStatus("authenticated");
      return data.admin || null;
    } catch {
      setAdmin(null);
      setStatus("unauthenticated");
      return null;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await fetch(`/api/admin/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch {
      /* ignore network errors on logout; local state is authoritative after this */
    }
    setAdmin(null);
    setStatus("unauthenticated");
  }, []);

  useEffect(() => {
    // The admin area is English-only by design. Force the document direction
    // so admins don't inherit RTL from a prior /ar visit.
    if (typeof document !== "undefined") {
      const html = document.documentElement;
      const prevLang = html.getAttribute("lang");
      const prevDir = html.getAttribute("dir");
      html.setAttribute("lang", "en");
      html.setAttribute("dir", "ltr");
      return () => {
        // Restore whatever the public LanguageProvider had set, so leaving the
        // admin area (e.g. clicking "Back to the public site") doesn't leave
        // the Arabic pages accidentally LTR.
        if (prevLang != null) html.setAttribute("lang", prevLang);
        if (prevDir != null) html.setAttribute("dir", prevDir);
      };
    }
    return undefined;
  }, []);

  useEffect(() => {
    // If we are returning from Emergent OAuth the fragment will contain a
    // session_id. AdminAuthCallback handles that exchange — skip /me here
    // so we do not race it and flip to "unauthenticated" momentarily.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setStatus("loading");
      return;
    }
    refresh();
  }, [refresh]);

  return (
    <AdminAuthContext.Provider value={{ admin, status, refresh, logout }}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error("useAdminAuth must be used within <AdminAuthProvider>");
  return ctx;
}
